/**
 * Simple isset method for this does not exist in javascript
 *
 * @param obj
 * @returns {boolean}
 */
export function isset(obj) {
    return typeof obj !== 'undefined' && obj !== null;
}
/**
 * Simple empty method for this does not exist in javascript
 *
 * @param obj
 * @returns {boolean}
 */
export function empty(obj) {
    return typeof obj === 'undefined' || obj === null || obj === '';
}
/**
 * Simple in_array method for this does not exist in javascript
 *
 * @param needle
 * @param haystack
 * @returns {boolean}
 */
export function in_array(needle, haystack) {
    return haystack.indexOf(needle) !== -1;
}
/**
 * Easing Functions - inspired from http://gizma.com/easing/
 * only considering the t value for the range [0, 1] => [0, 1]
 *
 * @type {{easeOutCubic: (function(*)), linear: (function(*): *), easeOutQuint: (function(*)), easeInQuart: (function(*)), easeInOutQuint: (function(*): number), easeInQuad: (function(*)), easeOutQuart: (function(*)), easeInCubic: (function(*)), easeInQuint: (function(*)), easeOutQuad: (function(*)), easeInOutQuad: (function(*): number), easeInOutCubic: (function(*): number), easeInOutQuart: (function(*): number)}}
 */
export const EasingFunctions = {
    // no easing, no acceleration
    linear: function (t) {
        return t;
    },
    // accelerating from zero velocity
    easeInQuad: function (t) {
        return t * t;
    },
    // decelerating to zero velocity
    easeOutQuad: function (t) {
        return t * (2 - t);
    },
    // acceleration until halfway, then deceleration
    easeInOutQuad: function (t) {
        return t < .5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    },
    // accelerating from zero velocity
    easeInCubic: function (t) {
        return t * t * t;
    },
    // decelerating to zero velocity
    easeOutCubic: function (t) {
        return (--t) * t * t + 1;
    },
    // acceleration until halfway, then deceleration
    easeInOutCubic: function (t) {
        return t < .5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
    },
    // accelerating from zero velocity
    easeInQuart: function (t) {
        return t * t * t * t;
    },
    // decelerating to zero velocity
    easeOutQuart: function (t) {
        return 1 - (--t) * t * t * t;
    },
    // acceleration until halfway, then deceleration
    easeInOutQuart: function (t) {
        return t < .5 ? 8 * t * t * t * t : 1 - 8 * (--t) * t * t * t;
    },
    // accelerating from zero velocity
    easeInQuint: function (t) {
        return t * t * t * t * t;
    },
    // decelerating to zero velocity
    easeOutQuint: function (t) {
        return 1 + (--t) * t * t * t * t;
    },
    // acceleration until halfway, then deceleration
    easeInOutQuint: function (t) {
        return t < .5 ? 16 * t * t * t * t * t : 1 + 16 * (--t) * t * t * t * t;
    }
};
/**
 * Map a given value from withing a range to another, so called interpolation.
 * So for example: 40 in range from 0 - 100, to range of 0 - 1, will give 0.4
 *
 * @param value
 * @param low1
 * @param high1
 * @param low2
 * @param high2
 */
export function interpolation(value, low1, high1, low2, high2) {
    return low2 + (high2 - low2) * (value - low1) / (high1 - low1);
}
export function normalise(value) {
    const accents = "ÀÁÂÃÄÅĄĀāàáâãäåąßÒÓÔÕÕÖØŐòóôőõöøĎďDŽdžÈÉÊËĘèéêëęðÇçČčĆćÐÌÍÎÏĪìíîïīÙÚÛÜŰùűúûüĽĹŁľĺłÑŇŃňñńŔŕŠŚŞšśşŤťŸÝÿýŽŻŹžżźđĢĞģğ";
    const accentsOut = "AAAAAAAAaaaaaaaasOOOOOOOOoooooooDdDZdzEEEEEeeeeeeCcCcCcDIIIIIiiiiiUUUUUuuuuuLLLlllNNNnnnRrSSSsssTtYYyyZZZzzzdGGgg";
    return value
        .split("")
        .map((letter, index) => {
        const accentIndex = accents.indexOf(letter);
        return accentIndex !== -1 ? accentsOut[accentIndex] : letter;
    })
        .join("");
}
/**
 * Deep clone a value
 * This could either be an array as an object.
 *
 * @param value
 * @returns cloneValue
 */
export function deepClone(value) {
    if (value === undefined)
        return undefined;
    return JSON.parse(JSON.stringify(value));
}
/**
 * @param func
 * @param wait
 * @returns {(function(...[*]=): void)|*}
 */
export function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}
/**
 * Javascript version of the number_format method of PHP
 *
 * @param number
 * @param decimals
 * @param dec_point
 * @param thousands_sep
 */
export function number_format(number, decimals, dec_point, thousands_sep) {
    // Strip all characters but numerical ones.
    number = (number + '').replace(/[^0-9+\-Ee.]/g, '');
    let n = !isFinite(+number) ? 0 : +number, prec = !isFinite(+decimals) ? 0 : Math.abs(decimals), sep = (typeof thousands_sep === 'undefined') ? ' ' : thousands_sep, dec = (typeof dec_point === 'undefined') ? ',' : dec_point, s = '', toFixedFix = function (n, prec) {
        var k = Math.pow(10, prec);
        return '' + Math.round(n * k) / k;
    };
    // Fix for IE parseFloat(0.55).toFixed(0) = 0;
    const sn = (prec ? toFixedFix(n, prec) : '' + Math.round(n)).split('.');
    if (sn[0].length > 3) {
        sn[0] = sn[0].replace(/\B(?=(?:\d{3})+(?!\d))/g, sep);
    }
    if ((sn[1] || '').length < prec) {
        sn[1] = sn[1] || '';
        sn[1] += new Array(prec - sn[1].length + 1).join('0');
    }
    return sn.join(dec);
}
/**
 * Resolve a nested property out of an object
 * Example: product.category.name
 *
 * @param object
 * @param key
 */
export function getNestedValue(object, key) {
    let currentProp = object;
    key.split('.').forEach((levelKey) => {
        if (currentProp === undefined)
            return;
        currentProp = currentProp[levelKey];
    });
    return currentProp;
}
